import React, { useRef, useEffect, useState, useCallback } from 'react'
import { evaluateGameStats, subscribeAchievementUnlocked } from './achievements'
import { playOrbSound, playHitSound, playTrophySound } from './audio'

export function PolarSurfer({ polar, demo, hrv, onExit }) {
  const canvasRef = useRef(null)

  // Estados de interfaz
  const [gameState, setGameState] = useState('menu') // 'menu' | 'playing' | 'gameover'
  const [score, setScore] = useState(0)
  const [coins, setCoins] = useState(0)
  const [highScore, setHighScore] = useState(() => {
    try { return parseInt(localStorage.getItem('manabi_surfer_highscore') || '0', 10) } catch { return 0 }
  })
  const [lives, setLives] = useState(3)
  const [unlockedToast, setUnlockedToast] = useState(null)
  const [lastStats, setLastStats] = useState(null)

  // Referencias para el bucle 60 FPS
  const stateRef = useRef({
    lane: 1, // 0 = Izquierda, 1 = Centro, 2 = Derecha
    laneX: 0, // Posición interpolada suavemente (-1 a 1)
    jumpY: 0, // Altura del salto (0 = suelo)
    jumpVy: 0,
    isJumping: false,
    speed: 12,
    distance: 0,
    score: 0,
    coins: 0,
    lives: 3,
    items: [], // Monedas y vallas
    particles: [],
    calmSeconds: 0,
    calmTimer: 0,
    bodyTiltLane: 1,
    lastJumpDetectTs: 0,
    startTime: 0,
    currentHr: 72,
    magnetActive: true,
  })

  // Pulsaciones y acelerometría en vivo
  const liveHr = (polar && polar.status === 'connected' && polar.hr)
    ? polar.hr
    : (demo && demo.active && demo.hr)
      ? demo.hr
      : 72

  const liveSteps = (polar && polar.steps) || (demo && demo.steps) || 0
  const isBleConnected = (polar && polar.status === 'connected') || (demo && demo.active)

  // Detección física con el cuerpo: Inclinación en Y y Salto en X
  useEffect(() => {
    const s = stateRef.current
    s.currentHr = liveHr
    s.magnetActive = liveHr <= 75

    // Lectura del acelerómetro Polar H10 (50 Hz)
    const accYList = polar?.accSamples?.current?.y || []
    const accXList = polar?.accSamples?.current?.x || []

    if (accYList.length > 0) {
      // Inclinación lateral en eje Y (-40 izquierda, +40 derecha)
      const recentY = accYList.slice(-5)
      const avgY = recentY.reduce((a, b) => a + b, 0) / recentY.length

      if (avgY < -42) {
        s.bodyTiltLane = 0 // Inclinación izquierda
        s.lane = 0
      } else if (avgY > 42) {
        s.bodyTiltLane = 2 // Inclinación derecha
        s.lane = 2
      } else {
        s.bodyTiltLane = 1 // Tronco centrado
        s.lane = 1
      }
    }

    if (accXList.length > 0) {
      // Detección de salto en eje X (pico vertical de despegue)
      const lastX = accXList[accXList.length - 1]
      const now = Date.now()
      if (lastX > 310 && !s.isJumping && now - s.lastJumpDetectTs > 750) {
        s.lastJumpDetectTs = now
        s.isJumping = true
        s.jumpVy = 13
      }
    }
  }, [liveHr, polar?.accSamples?.current?.x?.length, polar?.accSamples?.current?.y?.length])

  // Suscripción a trofeos
  useEffect(() => {
    const unsub = subscribeAchievementUnlocked(ach => {
      playTrophySound()
      setUnlockedToast(ach)
      setTimeout(() => setUnlockedToast(null), 3600)
    })
    return unsub
  }, [])

  // Iniciar partida
  const startGame = useCallback(() => {
    stateRef.current = {
      lane: 1,
      laneX: 0,
      jumpY: 0,
      jumpVy: 0,
      isJumping: false,
      speed: 13,
      distance: 0,
      score: 0,
      coins: 0,
      lives: 3,
      items: [],
      particles: [],
      calmSeconds: 0,
      calmTimer: 0,
      bodyTiltLane: 1,
      lastJumpDetectTs: 0,
      startTime: Date.now(),
      currentHr: liveHr,
      magnetActive: liveHr <= 75,
    }
    setScore(0)
    setCoins(0)
    setLives(3)
    setGameState('playing')
  }, [liveHr])

  // Controles de teclado manuales (Flechas / WASD)
  useEffect(() => {
    const onKeyDown = (e) => {
      const s = stateRef.current
      if (e.code === 'ArrowLeft' || e.code === 'KeyA') {
        s.lane = Math.max(0, s.lane - 1)
      } else if (e.code === 'ArrowRight' || e.code === 'KeyD') {
        s.lane = Math.min(2, s.lane + 1)
      } else if ((e.code === 'ArrowUp' || e.code === 'Space' || e.code === 'KeyW') && !s.isJumping) {
        s.isJumping = true
        s.jumpVy = 13
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  // Controles táctiles por Swipe
  const touchStartRef = useRef({ x: 0, y: 0 })
  const handleTouchStart = (e) => {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
  }
  const handleTouchEnd = (e) => {
    const s = stateRef.current
    const dx = e.changedTouches[0].clientX - touchStartRef.current.x
    const dy = e.changedTouches[0].clientY - touchStartRef.current.y
    if (Math.abs(dx) > Math.abs(dy)) {
      if (dx < -30) s.lane = Math.max(0, s.lane - 1)
      else if (dx > 30) s.lane = Math.min(2, s.lane + 1)
    } else {
      if (dy < -30 && !s.isJumping) {
        s.isJumping = true
        s.jumpVy = 13
      }
    }
  }

  // ── Bucle del Juego 3D en Canvas a 60 FPS ──
  useEffect(() => {
    if (gameState !== 'playing') return

    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    let animId = null
    let lastTime = performance.now()
    let spawnTimer = 0

    const loop = (now) => {
      const dt = Math.min(50, now - lastTime) / 1000
      lastTime = now

      const s = stateRef.current
      const width = canvas.width
      const height = canvas.height

      // Biofeedback de calma
      const isCalm = s.currentHr <= 75
      s.magnetActive = isCalm
      const mult = isCalm ? 3 : 1

      if (isCalm) {
        s.calmTimer += dt
        if (s.calmTimer >= 1) {
          s.calmSeconds += 1
          s.calmTimer = 0
        }
      }

      // Física del salto
      if (s.isJumping) {
        s.jumpY += s.jumpVy * 25 * dt
        s.jumpVy -= 32 * dt
        if (s.jumpY <= 0) {
          s.jumpY = 0
          s.jumpVy = 0
          s.isJumping = false
        }
      }

      // Interpolación suave del carril (-1 a 1)
      const targetX = s.lane === 0 ? -1 : s.lane === 2 ? 1 : 0
      s.laneX += (targetX - s.laneX) * Math.min(1, 14 * dt)

      // Avance de distancia y velocidad
      s.distance += s.speed * dt
      s.score += Math.round(s.speed * dt * mult * 2)
      setScore(s.score)

      // Generación de elementos (Monedas y Vallas)
      spawnTimer += dt
      if (spawnTimer > (1.1 - Math.min(0.4, s.distance * 0.0003))) {
        spawnTimer = 0
        const itemLane = Math.floor(Math.random() * 3) // 0, 1, 2
        const isHazard = Math.random() > 0.45
        s.items.push({
          z: 320, // Distancia en el horizonte
          lane: itemLane,
          type: isHazard ? 'barrier' : 'coin',
        })
      }

      // Posiciones de los 3 carriles en pantalla
      const horizonY = height * 0.32
      const groundY = height * 0.88
      const centerBottomX = width * 0.5
      const laneSpread = width * 0.38

      // Actualizar y chequear colisiones de ítems
      for (let i = s.items.length - 1; i >= 0; i--) {
        const item = s.items[i]
        item.z -= s.speed * 18 * dt

        // Imán de calma: si estás relajado (<75 BPM), las monedas vuelan a tu carril
        if (item.type === 'coin' && s.magnetActive && item.z < 160) {
          item.lane += (s.lane - item.lane) * Math.min(1, 8 * dt)
        }

        // Colisión cerca del jugador (z entre 15 y 45)
        if (item.z <= 45 && item.z >= 10) {
          const itemLaneX = item.lane === 0 ? -1 : item.lane === 2 ? 1 : 0
          const distToPlayer = Math.abs(itemLaneX - s.laneX)

          if (item.type === 'coin' && distToPlayer < 0.45) {
            playOrbSound()
            s.coins += 1
            s.score += 25 * mult
            setCoins(s.coins)

            // Partículas doradas
            for (let p = 0; p < 6; p++) {
              s.particles.push({
                x: centerBottomX + s.laneX * laneSpread,
                y: groundY - 30 - s.jumpY,
                vx: (Math.random() - 0.5) * 120,
                vy: (Math.random() - 0.5) * 120,
                color: '#ffd700',
                life: 0.3
              })
            }
            s.items.splice(i, 1)
            continue
          }

          if (item.type === 'barrier' && distToPlayer < 0.45) {
            // Si el jugador saltó por encima de la valla, esquivó con éxito
            if (s.jumpY > 32) {
              // Esquivado con éxito por el aire
            } else {
              // Impacto contra la valla
              playHitSound()
              s.lives -= 1
              setLives(s.lives)

              // Partículas rojas de impacto
              for (let p = 0; p < 10; p++) {
                s.particles.push({
                  x: centerBottomX + s.laneX * laneSpread,
                  y: groundY - 20,
                  vx: (Math.random() - 0.5) * 180,
                  vy: (Math.random() - 0.5) * 180,
                  color: '#ff3b30',
                  life: 0.4
                })
              }
              s.items.splice(i, 1)

              if (s.lives <= 0) {
                const durationSec = Math.round((Date.now() - s.startTime) / 1000)
                const stats = {
                  score: s.score,
                  coins: s.coins,
                  distance: Math.round(s.distance),
                  calmSec: s.calmSeconds,
                  durationSec,
                  isConnected: isBleConnected
                }
                setLastStats(stats)
                evaluateGameStats({
                  score: s.score,
                  durationSec,
                  calmSec: s.calmSeconds,
                  orbs: s.coins,
                  hits: 3 - s.lives,
                  isConnected: isBleConnected,
                  maxRmssd: 55
                })

                if (s.score > highScore) {
                  setHighScore(s.score)
                  try { localStorage.setItem('manabi_surfer_highscore', String(s.score)) } catch (_) {}
                }
                setGameState('gameover')
                return
              }
              continue
            }
          }
        }

        if (item.z <= 0) s.items.splice(i, 1)
      }

      // Actualizar partículas
      for (let i = s.particles.length - 1; i >= 0; i--) {
        const p = s.particles[i]
        p.x += p.vx * dt
        p.y += p.vy * dt
        p.life -= dt
        if (p.life <= 0) s.particles.splice(i, 1)
      }

      // ── RENDERIZADO VISUAL EN PERSPECTIVA 3D ──
      ctx.clearRect(0, 0, width, height)

      // Cielo nocturno futurista
      const skyGrad = ctx.createLinearGradient(0, 0, 0, horizonY)
      skyGrad.addColorStop(0, '#04060a')
      skyGrad.addColorStop(1, '#0c1220')
      ctx.fillStyle = skyGrad
      ctx.fillRect(0, 0, width, horizonY)

      // Suelo / Pista 3D
      const groundGrad = ctx.createLinearGradient(0, horizonY, 0, height)
      groundGrad.addColorStop(0, '#0e1628')
      groundGrad.addColorStop(1, '#050810')
      ctx.fillStyle = groundGrad
      ctx.fillRect(0, horizonY, width, height - horizonY)

      // Líneas de los 3 carriles convergiendo al horizonte
      ctx.strokeStyle = isCalm ? '#007aff55' : '#ff9f0a45'
      ctx.lineWidth = 1.5

      const lanesXTop = [-40, -12, 12, 40]
      const lanesXBottom = [-laneSpread * 1.5, -laneSpread * 0.5, laneSpread * 0.5, laneSpread * 1.5]

      for (let l = 0; l < 4; l++) {
        ctx.beginPath()
        ctx.moveTo(centerBottomX + lanesXTop[l], horizonY)
        ctx.lineTo(centerBottomX + lanesXBottom[l], groundY)
        ctx.stroke()
      }

      // Líneas de velocidad en el suelo que avanzan
      const lineOffset = (s.distance * 14) % 60
      ctx.strokeStyle = isCalm ? 'rgba(0,122,255,0.22)' : 'rgba(255,159,10,0.18)'
      ctx.lineWidth = 1
      for (let y = horizonY + lineOffset; y < groundY; y += 45) {
        const p = (y - horizonY) / (groundY - horizonY)
        const curSpread = laneSpread * 1.5 * p
        ctx.beginPath()
        ctx.moveTo(centerBottomX - curSpread, y)
        ctx.lineTo(centerBottomX + curSpread, y)
        ctx.stroke()
      }

      // Ordenar y dibujar ítems en perspectiva de profundidad (de lejos a cerca)
      s.items.sort((a, b) => b.z - a.z)

      s.items.forEach(item => {
        const p = Math.max(0.05, Math.min(1, 1 - (item.z / 320))) // 0 = horizonte, 1 = jugador
        const curY = horizonY + (groundY - horizonY) * p
        const curSpread = laneSpread * p
        const itemLaneX = item.lane === 0 ? -1 : item.lane === 2 ? 1 : 0
        const curX = centerBottomX + itemLaneX * curSpread
        const scale = p * 1.6

        if (item.type === 'coin') {
          // Moneda dorada brillante
          ctx.beginPath()
          ctx.arc(curX, curY - 14 * scale, 9 * scale, 0, Math.PI * 2)
          ctx.fillStyle = '#ffd700'
          ctx.shadowColor = '#ffd700'
          ctx.shadowBlur = 10 * scale
          ctx.fill()
          ctx.strokeStyle = '#fff'
          ctx.lineWidth = 1.5 * scale
          ctx.stroke()
          ctx.shadowBlur = 0
        } else if (item.type === 'barrier') {
          // Valla de salto roja luminosa
          const w = 48 * scale
          const h = 20 * scale
          ctx.fillStyle = '#ff3b30'
          ctx.shadowColor = '#ff3b30'
          ctx.shadowBlur = 12 * scale
          ctx.beginPath()
          ctx.roundRect ? ctx.roundRect(curX - w / 2, curY - h, w, h, 4 * scale) : ctx.rect(curX - w / 2, curY - h, w, h)
          ctx.fill()
          ctx.strokeStyle = '#ffffff'
          ctx.lineWidth = 2 * scale
          ctx.stroke()
          ctx.shadowBlur = 0
        }
      })

      // Dibujar Personaje (Surfer / Runner)
      const playerX = centerBottomX + s.laneX * laneSpread
      const playerY = groundY - 24 - s.jumpY

      // Sombra del jugador en el suelo
      ctx.beginPath()
      ctx.ellipse(playerX, groundY - 12, 18, 6, 0, 0, Math.PI * 2)
      ctx.fillStyle = 'rgba(0,0,0,0.45)'
      ctx.fill()

      // Aura de calma del Polar H10 (si estás relajado <75 BPM)
      if (s.magnetActive) {
        ctx.beginPath()
        ctx.arc(playerX, playerY, 28, 0, Math.PI * 2)
        ctx.fillStyle = 'rgba(0, 122, 255, 0.18)'
        ctx.strokeStyle = '#64d2ff'
        ctx.lineWidth = 1.5
        ctx.setLineDash([4, 4])
        ctx.stroke()
        ctx.setLineDash([])
      }

      // Cuerpo del Surfer
      ctx.beginPath()
      ctx.arc(playerX, playerY, 15, 0, Math.PI * 2)
      ctx.fillStyle = isCalm ? '#64d2ff' : '#ffd60a'
      ctx.shadowColor = isCalm ? '#64d2ff' : '#ffd60a'
      ctx.shadowBlur = 16
      ctx.fill()
      ctx.strokeStyle = '#ffffff'
      ctx.lineWidth = 2.5
      ctx.stroke()
      ctx.shadowBlur = 0

      // Núcleo del sensor Polar en el pecho
      ctx.beginPath()
      ctx.arc(playerX, playerY, 5, 0, Math.PI * 2)
      ctx.fillStyle = '#ff3b30'
      ctx.fill()

      // Dibujar partículas
      s.particles.forEach(p => {
        ctx.beginPath()
        ctx.arc(p.x, p.y, 3.5, 0, Math.PI * 2)
        ctx.fillStyle = p.color
        ctx.fill()
      })

      animId = requestAnimationFrame(loop)
    }

    animId = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(animId)
  }, [gameState, highScore, isBleConnected])

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{
        position: 'relative', width: '100%', maxWidth: 840, margin: '0 auto',
        background: '#07090e', borderRadius: 18, border: '1px solid rgba(255,255,255,0.12)',
        overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        userSelect: 'none', WebkitUserSelect: 'none', touchAction: 'none'
      }}
    >
      {/* ── 1. HUD Superior en Vivo ── */}
      <div style={{
        padding: '10px 16px', background: 'rgba(255,255,255,0.04)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 10
      }}>
        {/* Pulsaciones e Imán de Calma */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 9, height: 9, borderRadius: '50%',
            background: liveHr <= 75 ? '#30d158' : '#ff9f0a',
            boxShadow: `0 0 8px ${liveHr <= 75 ? '#30d158' : '#ff9f0a'}`,
          }} />
          <span style={{ fontSize: 13, fontWeight: 900, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
            {liveHr} <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>BPM</span>
          </span>
          <span style={{
            fontSize: 9.5, fontWeight: 800, padding: '2px 7px', borderRadius: 6,
            background: liveHr <= 75 ? 'rgba(0,122,255,0.25)' : 'rgba(255,159,10,0.2)',
            color: liveHr <= 75 ? '#64d2ff' : '#ff9f0a'
          }}>
            {liveHr <= 75 ? 'IMÁN ACTIVO x3' : 'PULSO ALTO x1'}
          </span>
        </div>

        {/* Monedas y Puntuación */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
          <span style={{ fontSize: 13, fontWeight: 800, color: '#ffd700', display: 'flex', alignItems: 'center', gap: 4 }}>
            <span>●</span> {coins}
          </span>
          <span style={{ fontSize: 19, fontWeight: 900, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
            {score} <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>PTS</span>
          </span>
        </div>

        {/* Vidas y Salir */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          {[1, 2, 3].map(i => (
            <div
              key={i}
              style={{
                width: 9, height: 9, borderRadius: '50%',
                background: i <= lives ? '#ff3b30' : 'rgba(255,255,255,0.15)',
                boxShadow: i <= lives ? '0 0 6px #ff3b30' : 'none'
              }}
            />
          ))}
          <button
            onClick={onExit}
            style={{
              background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 6,
              color: 'rgba(255,255,255,0.7)', fontSize: 11, fontWeight: 700,
              padding: '3px 8px', cursor: 'pointer', fontFamily: 'inherit', marginLeft: 8
            }}
          >
            ✕ Salir
          </button>
        </div>
      </div>

      {/* ── 2. Canvas 3D de la Pista ── */}
      <div style={{ position: 'relative', width: '100%', height: 420 }}>
        <canvas
          ref={canvasRef}
          width={840}
          height={420}
          style={{ width: '100%', height: '100%', display: 'block' }}
        />

        {/* Notificación de logro desbloqueado */}
        {unlockedToast && (
          <div style={{
            position: 'absolute', top: 16, right: 16,
            background: 'rgba(15, 20, 32, 0.92)', border: '1.5px solid #ffd700',
            borderRadius: 12, padding: '8px 14px', boxShadow: '0 8px 28px rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', gap: 10,
            animation: 'fadeIn 0.25s ease', backdropFilter: 'blur(12px)', zIndex: 30
          }}>
            <span style={{ fontSize: 16 }}>★</span>
            <div>
              <span style={{ fontSize: 9.5, fontWeight: 800, color: '#ffd700', textTransform: 'uppercase', letterSpacing: 0.6, display: 'block' }}>
                ¡Trofeo Desbloqueado!
              </span>
              <span style={{ fontSize: 12, fontWeight: 900, color: '#fff' }}>
                {unlockedToast.title}
              </span>
            </div>
          </div>
        )}

        {/* ── Menú de Inicio ── */}
        {gameState === 'menu' && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(circle, rgba(15,20,32,0.92) 0%, rgba(6,8,14,0.98) 100%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: 24, textAlign: 'center', zIndex: 20
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', background: 'rgba(0,122,255,0.18)',
              border: '2px solid #007aff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 0 24px rgba(0,122,255,0.3)', marginBottom: 12
            }}>
              <span style={{ fontSize: 24, color: '#007aff' }}>▶</span>
            </div>
            <h2 style={{ fontSize: 25, fontWeight: 900, color: '#fff', margin: '0 0 6px', letterSpacing: -0.5 }}>
              Polar Surfer: Bio-Runner 3D
            </h2>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', maxWidth: 460, margin: '0 0 16px', lineHeight: 1.45 }}>
              Estilo Subway Surfers controlado con el sensor Polar H10:
              <br />
              <strong style={{ color: '#007aff' }}>Incliná tu torso a los lados</strong> para cambiar de carril y <strong style={{ color: '#30d158' }}>pegá un salto</strong> para saltar vallas.
              <br />
              <strong style={{ color: '#ffd700' }}>Biofeedback:</strong> Mantené el pulso en calma (&lt;75 BPM) para activar el imán de monedas automático.
            </p>
            <button
              onClick={startGame}
              style={{
                background: '#007aff', color: '#fff', border: 'none',
                borderRadius: 12, padding: '12px 28px', fontSize: 14, fontWeight: 800,
                cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 18px rgba(0,122,255,0.4)'
              }}
            >
              ¡ARRANCAR A SURFEAR!
            </button>
          </div>
        )}

        {/* ── Game Over ── */}
        {gameState === 'gameover' && lastStats && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(circle, rgba(20,12,14,0.94) 0%, rgba(6,8,14,0.98) 100%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: 24, textAlign: 'center', zIndex: 20
          }}>
            <h2 style={{ fontSize: 26, fontWeight: 900, color: '#fff', margin: '0 0 12px' }}>
              Fin de la Carrera
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, width: '100%', maxWidth: 380, marginBottom: 18 }}>
              <div style={{ background: 'rgba(255,255,255,0.06)', padding: '8px', borderRadius: 8 }}>
                <span style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.5)', display: 'block' }}>Puntos</span>
                <span style={{ fontSize: 18, fontWeight: 900, color: '#64d2ff' }}>{lastStats.score}</span>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.06)', padding: '8px', borderRadius: 8 }}>
                <span style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.5)', display: 'block' }}>Monedas</span>
                <span style={{ fontSize: 18, fontWeight: 900, color: '#ffd700' }}>{lastStats.coins}</span>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.06)', padding: '8px', borderRadius: 8 }}>
                <span style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.5)', display: 'block' }}>Distancia</span>
                <span style={{ fontSize: 18, fontWeight: 900, color: '#30d158' }}>{lastStats.distance}m</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={startGame}
                style={{
                  background: '#007aff', color: '#fff', border: 'none',
                  borderRadius: 10, padding: '10px 22px', fontSize: 13, fontWeight: 800,
                  cursor: 'pointer', fontFamily: 'inherit'
                }}
              >
                Surfear de Nuevo
              </button>
              <button
                onClick={() => setGameState('menu')}
                style={{
                  background: 'rgba(255,255,255,0.12)', color: '#fff', border: 'none',
                  borderRadius: 10, padding: '10px 18px', fontSize: 13, fontWeight: 700,
                  cursor: 'pointer', fontFamily: 'inherit'
                }}
              >
                Menú
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── 3. Instrucción de Controles al pie ── */}
      <div style={{
        padding: '8px 16px', background: 'rgba(255,255,255,0.02)',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: 'rgba(255,255,255,0.5)'
      }}>
        <span>Acelerómetro Polar H10: Inclinación lateral para carril · Salto en el pecho para valla</span>
        <span>Teclado: Flechas o A/D para carril · Espacio para saltar</span>
      </div>
    </div>
  )
}
